import type {
  QuartileEvent,
  WebediaPlayer
} from '@webedia-services/webedia-player';
import loadScript from 'load-script';

import { isProd } from 'common/tools/env';
import { getKeys } from 'common/types';

import {
  GEMIUS_LIB_URL,
  GEMIUS_PREFIX,
  GEMIUS_IDENTIFIER
} from 'website/configuration/constants';
import type { PlayerCustomData } from 'website/module/player';

declare global {
  interface Window {
    gSmDebug?: GSmDebug;
    gSmPlayer?: GSmPlayer;
    gSmStream?: GSmStream;
    gemiusStream2?: GemiusStream2;
    gemiusStream?: GemiusStream;
    gemius_cmpclient?: GemiusCmpclient;
    gemius_event?: GemiusEvent;
    gemius_hcconn?: GemiusHcconn;
    gemius_hit?: GemiusHit;
    gemius_init?: GemiusInit;
    gemius_pending?: GemiusPending;
    gsm_gemius_escape?: (str: string, limit: number) => string;
    pp_gemius_event?: GemiusEvent;
    pp_gemius_hit?: GemiusHit;
    pp_gemius_init?: GemiusInit;
  }
}

type SendObject = {
  action: EventType;
  streamVal: { [K in keyof GemiusStreamEventArgs]: GemiusStreamEventArgs[K] };
};

/**
 * This flag is used to determine whether gemius's lib script was loaded on the page
 * true: we start to send hit to gemius
 * false: we save push hit into a temporary stack and will send it later in the next hit request
 * @type {Boolean}
 */
let libLoaded = false;
/**
 * This is a temporary stack to store the hits
 * @type {Array}
 */
let trackerStacks: SendObject[] = [];

/**
 * This object is used to generate the value of streaming config in each circumstance
 * @type {Object}
 */
const configStreaming: {
  [K in keyof GemiusStreamEventArgs]: {
    getValue: (
      webediaPlayer: WebediaPlayer,
      customData: PlayerCustomData
    ) => GemiusStreamEventArgs[K];
  };
} = {
  playerId: {
    getValue: webediaPlayer => webediaPlayer.getPlayerId()
  },
  contentId: {
    getValue: (webediaPlayer, { id }) =>
      (
        (webediaPlayer.postroll?.isPlayingPostrollVideo()
          ? webediaPlayer.postroll?.getCurrentPostroll()?.cmedia
          : id) ?? ''
      ).toString()
  },
  time: {
    getValue: webediaPlayer => Math.round(webediaPlayer.getPosition() ?? 0)
  }
};

/**
 * This function is used to set streaming object and send hit by using gemius api
 * @param  {String} action    The hit action's name (play, pause, stop)
 * @return {void}
 */
const send = ({
  action: eventType,
  streamVal: { playerId, contentId, time }
}: SendObject) => {
  if (isProd() && window.gemiusStream) {
    window.gemiusStream.event(playerId, contentId, time, eventType);
  }
};

/**
 * This function is used to generate a streaming object and determine whether we save or send this hit
 * @param {WebediaPlayer} webediaPlayer
 * @param  {String} eventAction The hit action's name (play, pause, stop)
 * @param {PlayerCustomData} customData
 * @return {void}
 */
export const trackerHandler = (
  webediaPlayer: WebediaPlayer,
  eventAction: EventType,
  customData: PlayerCustomData
) => {
  const streamingObj: SendObject = {
    action: eventAction,
    streamVal: {
      playerId: undefined,
      contentId: undefined,
      time: 0
    }
  };
  getKeys(configStreaming).forEach(value => {
    streamingObj.streamVal = {
      ...streamingObj.streamVal,
      [value]: configStreaming[value].getValue(webediaPlayer, customData)
    };
  });

  if (libLoaded && typeof window.gemiusStream !== 'undefined') {
    for (const tracker of trackerStacks) {
      send(tracker);
    }

    trackerStacks = [];
    send(streamingObj);
  } else {
    trackerStacks.push(streamingObj);
  }
};

export const quartileEventTracking = (
  webediaPlayer: WebediaPlayer,
  { detail: { quartile } }: QuartileEvent,
  customData: PlayerCustomData
) => {
  if (quartile === 0) {
    if (libLoaded) {
      const playerId = webediaPlayer.getPlayerId();
      const { id: materialIdentifier, title: cName } = customData;

      window.gemiusStream?.newStream(
        playerId,
        materialIdentifier?.toString(),
        webediaPlayer.getDuration() ?? 0,
        [
          { name: 'CNAME', value: cName ?? undefined },
          { name: 'cmedia', value: '' + materialIdentifier }
        ],
        [],
        GEMIUS_IDENTIFIER,
        `${window.location.protocol}//${GEMIUS_PREFIX}.hit.gemius.pl`,
        [100]
      );
    }

    trackerHandler(webediaPlayer, 'playing', customData);
  } else if (quartile === 4) {
    trackerHandler(webediaPlayer, 'stopped', customData);
  }
};

/**
 * This function is used to asynchronously load gemius lib,
 * set a flag libLoaded to true and init gemius api 'http://str.hit.gemius.pl/gstream.js'
 * @return {void}
 */
export function loadGemiusLib() {
  if (typeof window.gemiusStream === 'undefined') {
    loadScript(GEMIUS_LIB_URL, () => {
      libLoaded = true;
    });
  }
}
